/**
 * Dashboard API
 * API client for all dashboard endpoints
 * Base URL: /api/v1/dashboard
 *
 * Uses mock data with feature flag for development
 */

import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  FleetSummaryApiResponse,
  FleetStatusApiResponse,
  FleetMapApiResponse,
  ConnectivityIssuesApiResponse,
  EnergyAnalyticsApiResponse,
  RevenueAnalyticsApiResponse,
  TopLocationsApiResponse,
  ActiveSessionsApiResponse,
  RecentSessionsApiResponse,
  AlertsSummaryApiResponse,
  AlertsApiResponse,
  AccountsSummaryApiResponse,
  SubscriptionsApiResponse,
  PaymentsTodayApiResponse,
  FleetSummaryParams,
  FleetStatusParams,
  ConnectivityIssuesParams,
  AnalyticsParams,
  TopLocationsParams,
  ActiveSessionsParams,
  RecentSessionsParams,
  AlertsParams,
  SubscriptionsParams,
} from '../types/api-types';

// Import mock data generators
import { getMockFleetSummary, getMockFleetStatus } from './__mocks__/fleet-mock-data';
import { getMockEnergyAnalytics, getMockRevenueAnalytics, getMockTopLocations } from './__mocks__/analytics-mock-data';
import { getMockActiveSessions, getMockRecentSessions } from './__mocks__/sessions-mock-data';
import { getMockAlertsSummary, getMockAlerts } from './__mocks__/alerts-mock-data';
import { getMockAccountSummary, getMockSubscriptions, getMockPaymentsToday } from './__mocks__/accounts-mock-data';

// Feature flag: Set to false to use real API, true for mock data
const USE_MOCK_DATA = false;

// API base path (appended to BASE_AUTH_OFFICE_API which already includes /api/v1)
const DASHBOARD_API_PATH = '/dashboard';

export class DashboardApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async buildHeaders(): Promise<Record<string, string>> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const operator = useOperatorStore.getState().selectedOperator;

    return {
      ...authHeaders,
      'Content-Type': 'application/json',
      ...(operator?.operatorId && { 'X-Operator-Id': operator.operatorId.toString() }),
    };
  }

  /**
   * Build query string from params, handling actorIds array
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;

      if (key === 'actorIds' && Array.isArray(value)) {
        // Join actorIds as comma-separated string
        if (value.length > 0) {
          searchParams.set(key, value.join(','));
        }
      } else if (typeof value === 'boolean') {
        searchParams.set(key, value.toString());
      } else if (typeof value === 'number') {
        searchParams.set(key, value.toString());
      } else if (typeof value === 'string') {
        searchParams.set(key, value);
      }
    }

    return searchParams.toString();
  }

  // ============================================
  // Fleet Endpoints
  // GET /fleet/summary?actorIds={comma-separated-ids}
  // GET /fleet/status?actorIds={ids}&search={text}&onlineOnly={bool}&skip={n}&take={n}
  // GET /fleet/connectivity-issues?minHoursOffline={hours}
  // ============================================

  async getFleetSummary(params?: FleetSummaryParams): Promise<FleetSummaryApiResponse> {
    // Only include actorIds if explicitly provided (these should be location ActorIds, not OperatorId)
    // When omitted, the API uses the OperatorId from the JWT token to return all operator data
    const actorIds = params?.actorIds;

    if (USE_MOCK_DATA) {
      await this.simulateDelay(300);
      return getMockFleetSummary();
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      ...(actorIds && { actorIds }),
      period: params?.period,
      from: params?.from,
      to: params?.to,
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/fleet/summary?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/fleet/summary'
    );
    return response.json();
  }

  async getFleetStatus(params?: FleetStatusParams): Promise<FleetStatusApiResponse> {
    // Only include actorIds if explicitly provided (these should be location ActorIds, not OperatorId)
    // When omitted, the API uses the OperatorId from the JWT token to return all operator data
    const actorIds = params?.actorIds;

    if (USE_MOCK_DATA) {
      await this.simulateDelay(400);
      // Map params to mock data format
      const filters: Record<string, string> = {};
      if (params?.search) filters.searchTerm = params.search;
      if (params?.onlineOnly) filters.status = 'Online';

      const page = params?.skip !== undefined && params?.take !== undefined
        ? Math.floor(params.skip / params.take)
        : 0;
      const pageSize = params?.take ?? 20;

      return getMockFleetStatus(page, pageSize, filters);
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      ...(actorIds && { actorIds }),
      search: params?.search,
      onlineOnly: params?.onlineOnly,
      machineStatus: params?.machineStatus,
      connectorStatus: params?.connectorStatus,
      sessionStatus: params?.sessionStatus,
      skip: params?.skip ?? 0,
      take: params?.take ?? 20,
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/fleet/status?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/fleet/status'
    );
    return response.json();
  }

  async getFleetMap(): Promise<FleetMapApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(300);
      return { points: [] };
    }

    const headers = await this.buildHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/fleet/map`,
      { method: 'GET', headers },
      'Dashboard API',
      '/fleet/map'
    );
    return response.json();
  }

  async getConnectivityIssues(params?: ConnectivityIssuesParams): Promise<ConnectivityIssuesApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(300);
      // Return empty for mock data - connectivity issues come from alerts
      return { items: [], totalCount: 0 };
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      minHoursOffline: params?.minHoursOffline ?? 24,
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/fleet/connectivity-issues?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/fleet/connectivity-issues'
    );
    return response.json();
  }

  // ============================================
  // Analytics Endpoints
  // GET /analytics/energy?actorIds={ids}&from={date}&to={date}&groupBy={day|week|month}
  // GET /analytics/revenue?actorIds={ids}&from={date}&to={date}&groupBy={day|week|month}
  // GET /analytics/top-locations?from={date}&to={date}&limit={n}
  // ============================================

  async getEnergyAnalytics(params: AnalyticsParams): Promise<EnergyAnalyticsApiResponse> {
    // Only include actorIds if explicitly provided (these should be location ActorIds, not OperatorId)
    // When omitted, the API uses the OperatorId from the JWT token to return all operator data
    const actorIds = params.actorIds;

    if (USE_MOCK_DATA) {
      await this.simulateDelay(500);
      return getMockEnergyAnalytics(params.from, params.to);
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      ...(actorIds && { actorIds }),
      from: params.from,
      to: params.to,
      groupBy: params.groupBy ?? 'day',
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/analytics/energy?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/analytics/energy'
    );
    return response.json();
  }

  async getRevenueAnalytics(params: AnalyticsParams): Promise<RevenueAnalyticsApiResponse> {
    // Only include actorIds if explicitly provided (these should be location ActorIds, not OperatorId)
    // When omitted, the API uses the OperatorId from the JWT token to return all operator data
    const actorIds = params.actorIds;

    if (USE_MOCK_DATA) {
      await this.simulateDelay(500);
      return getMockRevenueAnalytics(params.from, params.to);
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      ...(actorIds && { actorIds }),
      from: params.from,
      to: params.to,
      groupBy: params.groupBy ?? 'day',
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/analytics/revenue?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/analytics/revenue'
    );
    return response.json();
  }

  async getTopLocations(params: TopLocationsParams): Promise<TopLocationsApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(300);
      return getMockTopLocations(params.limit ?? 10);
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      from: params.from,
      to: params.to,
      limit: params.limit ?? 10,
      sortBy: params.sortBy ?? 'revenue',
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/analytics/top-locations?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/analytics/top-locations'
    );
    return response.json();
  }

  // ============================================
  // Sessions Endpoints
  // GET /sessions/active?actorIds={ids}&search={text}
  // GET /sessions/recent?hours={n}&skip={n}&take={n}
  // ============================================

  async getActiveSessions(params?: ActiveSessionsParams): Promise<ActiveSessionsApiResponse> {
    // Only include actorIds if explicitly provided (these should be location ActorIds, not OperatorId)
    // When omitted, the API uses the OperatorId from the JWT token to return all operator data
    const actorIds = params?.actorIds;

    if (USE_MOCK_DATA) {
      await this.simulateDelay(300);
      return getMockActiveSessions();
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      ...(actorIds && { actorIds }),
      search: params?.search,
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/sessions/active?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/sessions/active'
    );
    return response.json();
  }

  async getRecentSessions(params?: RecentSessionsParams): Promise<RecentSessionsApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(400);
      const page = params?.skip !== undefined && params?.take !== undefined
        ? Math.floor(params.skip / params.take)
        : 0;
      const pageSize = params?.take ?? 50;
      const hours = params?.hours ?? 24;

      return getMockRecentSessions(page, pageSize, hours);
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      hours: params?.hours ?? 24,
      skip: params?.skip ?? 0,
      take: params?.take ?? 50,
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/sessions/recent?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/sessions/recent'
    );
    return response.json();
  }

  // ============================================
  // Alerts Endpoints
  // GET /alerts/summary
  // GET /alerts?type={connectivity|calculation|payment}&pendingOnly={bool}&skip={n}&take={n}
  // ============================================

  async getAlertsSummary(): Promise<AlertsSummaryApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(200);
      return getMockAlertsSummary();
    }

    const headers = await this.buildHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/alerts/summary`,
      { method: 'GET', headers },
      'Dashboard API',
      '/alerts/summary'
    );
    return response.json();
  }

  async getAlerts(params?: AlertsParams): Promise<AlertsApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(300);
      // Map params to mock data format
      const filters: Record<string, string> = {};
      if (params?.type) filters.type = params.type;
      if (params?.pendingOnly) filters.status = 'Active';

      return getMockAlerts(filters);
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      type: params?.type,
      pendingOnly: params?.pendingOnly,
      skip: params?.skip ?? 0,
      take: params?.take ?? 50,
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/alerts?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/alerts'
    );
    return response.json();
  }

  // ============================================
  // Accounts Endpoints
  // GET /accounts/summary
  // GET /accounts/subscriptions?expiringOnly={bool}&skip={n}&take={n}
  // GET /accounts/payments/today
  // ============================================

  async getAccountsSummary(): Promise<AccountsSummaryApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(200);
      return getMockAccountSummary();
    }

    const headers = await this.buildHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/accounts/summary`,
      { method: 'GET', headers },
      'Dashboard API',
      '/accounts/summary'
    );
    return response.json();
  }

  async getSubscriptions(params?: SubscriptionsParams): Promise<SubscriptionsApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(400);
      return getMockSubscriptions();
    }

    const headers = await this.buildHeaders();
    const queryString = this.buildQueryString({
      expiringOnly: params?.expiringOnly,
      skip: params?.skip ?? 0,
      take: params?.take ?? 50,
    });

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/accounts/subscriptions?${queryString}`,
      { method: 'GET', headers },
      'Dashboard API',
      '/accounts/subscriptions'
    );
    return response.json();
  }

  async getPaymentsToday(): Promise<PaymentsTodayApiResponse> {
    if (USE_MOCK_DATA) {
      await this.simulateDelay(300);
      return getMockPaymentsToday();
    }

    const headers = await this.buildHeaders();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${DASHBOARD_API_PATH}/accounts/payments/today`,
      { method: 'GET', headers },
      'Dashboard API',
      '/accounts/payments/today'
    );
    return response.json();
  }

  // ============================================
  // Utilities
  // ============================================

  /**
   * Simulate network delay for mock data
   */
  private simulateDelay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
